import React, { useEffect, useRef, useState } from 'react'
import { useHistory, Link } from 'react-router-dom'
import { useAuth } from '../contexts/Auth'
import { useTranslation } from 'react-i18next';
import { Grid } from "react-loader-spinner";

export function Login() {
  const { t } = useTranslation();
  const emailRef = useRef()
  const passwordRef = useRef()

  const { user, signIn } = useAuth()
  const history = useHistory()

  useEffect(() => {
    // redirect to homepage if already logged in
    if (user) {
      history.push('/')
    }
  }, [history, user]);

  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [gLoginErrorMessage, setGLoginErrorMessage] = useState("");
  const [gLoginLoading, setGLoginLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    // Get email and password input values
    const email = emailRef.current.value
    const password = passwordRef.current.value

    // Calls `signIn` function from the context
    const { error } = await signIn({ email, password })

    if (error) {
      setLoading(false);
      let errorText = t('text.loginErrorGeneric');
      if (error.status === 400) {
        errorText = (error.message === "Email not confirmed")
          ? t('text.loginErrorEmailNotConfirmed')
          : t('text.loginErrorInvalidCredentials');
      }
      setErrorMessage(errorText);
    } else {
      setLoading(false);
      // Redirect user to Dashboard
      history.push('/')
    }
  }

  const handleGoogleSignIn = async () => {
    setGLoginErrorMessage("");
    setGLoginLoading(true);
    const { error } = await signIn({ provider: 'google' })
    if (error) {
      setGLoginLoading(false);
      console.log("Error while trying to signIn through Google", error);
      setGLoginErrorMessage("Error during Google Sign up");
    }
  }

  return (
    <div className="container flex flex-col">
      <div className="max-w-xs mx-auto">
        <div className="max-w-sm bg-blue-300 shadow-md rounded-md px-8 pt-6 pb-8 mb-4 mt-4 ml-4">
          {gLoginErrorMessage ? <div className="text-red-600 text-center font-medium mb-4">{gLoginErrorMessage}</div> : ""}
          <div className="flex mt-2">
            <h3 className="text-md font-bold tracking-normal mr-4">{t('label.login')}</h3>
            {gLoginLoading && <span className="my-auto"><Grid type="Grid" color="#F9FAFB" height="14" width="14" /></span>}
          </div>
          <button disabled={gLoginLoading} className="flex items-center border-white bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-8 my-2 rounded-md focus:outline-none focus:shadow-outline" onClick={() => handleGoogleSignIn()}>
            <svg width="20" aria-hidden="true" data-prefix="fab" data-icon="google" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
              <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z" />
            </svg>
            <span className="ml-2">{t('button.googleSignUp')}</span>
          </button>
          <div className="sbui-divider my-6">
            <span className="font-normal text-gray-700 inline-block">{t('label.continueWith')}</span>
          </div>
          <form onSubmit={handleSubmit}>
            {errorMessage ? <div className="text-red-600 text-center font-medium mb-4">{errorMessage}</div> : ""}
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="input-email">
              {t('label.email')}
            </label>
            <div className="relative">
              <input className="shadow appearance-none border rounded-md shadow-sm w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline pl-10" id="input-email" type="email" ref={emailRef} />
              <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>

            <label className="block text-gray-700 text-sm font-bold mb-2 mt-4" htmlFor="input-password">
              {t('label.password')}
            </label>
            <div className="relative">
              <input className="shadow appearance-none border rounded-md shadow-sm w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline pl-10" id="input-password" type="password" ref={passwordRef} />
              <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
            </div>

            <div className="flex mt-4">
              <button disabled={loading} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 mr-4 rounded-md focus:outline-none focus:shadow-outline" type="submit">
                {t('button.login')}
              </button>
              {loading && <span className="my-auto"><Grid type="Grid" color="#F9FAFB" height="28" width="28" /></span>}
            </div>
          </form>
          <p className="mt-4">{t('text.accountClarification1')} <Link className="underline" to="/signup">{t('button.signup')}</Link></p>
        </div>
      </div>
    </div>
  )
}
